import Joi from "joi-browser";

function nameErrorsAsString(errors) {
    let stringError = "";
    const err = errors[0];
    switch (err.type) {
        case "string.max":
            stringError = `can't exceed ${err.context.limit} letters.`;
            break;
        case "string.min":
            stringError = `can't be less than ${err.context.limit} letters.`;
            break;
        case "any.empty":
            stringError = "can't be an empty field.";
            break;
        case "string.regex.base":
            stringError = "should be an alphabetical characters only.";
            break;
        default:
            stringError = "is required.";
    }

    return {
        template: " {{message}}",
        context: {
            errors: errors.length,
            message: stringError,
        },
    };
}

function eolErrors(errors) {
    let stringError = "";
    const err = errors[0];
    switch (err.type) {
        case "date.min":
            stringError = "must be more than today";
            break;
        case "any.empty":
            stringError = "can't be an empty field.";
            break;
        case "date.max":
            stringError = "can't exceed the provider's end of license";
            break;
        default:
            stringError = "is required.";
    }

    return {
        template: " {{message}}",
        context: {
            errors: errors.length,
            message: stringError,
        },
    };
}

function requiredErrorsAsString(errors) {
    return {
        template: " {{message}}",
        context: {
            errors: errors.length,
            message: "is required",
        },
    };
}

const AddOrganizationSchema = Joi.object().keys({
    name: Joi.string()
        .trim()
        .max(256)
        .min(3)
        .regex(/[\w[\]`!@#$%^&*()={}:;<>+'-]*/)
        .required()
        .label("Organization Name")
        .error((errors) => {
            return nameErrorsAsString(errors);
        }),
    maxUsersNumber: Joi.number()
        .integer()
        .min(1)
        .required()
        .label("Max Users Number")
        .error((errors) => {
            function errorsAsString() {
                const err = errors[0];
                switch (err.type) {
                    case "number.min":
                        return `can't be less than ${err.context.limit} users.`;
                    case "any.empty":
                        return "can't be an empty field.";
                    case "string.regex.base":
                        return "should be a number.";
                    case "number.max":
                        return "can not exceed the provider's max number of users";
                    default:
                        return "is required.";
                }
            }
            return {
                template: " {{message}}",
                context: {
                    errors: errors.length,
                    message: errorsAsString(),
                },
            };
        }),
    type: Joi.string()
        .required()
        .label("Type")
        .error((errors) => {
            return requiredErrorsAsString(errors);
        }),
    endOfLicense: Joi.when(Joi.ref("$isSuperAdmin"), {
        is: true,
        then: Joi.date()
            .min(Date.now())
            .required()
            .label("End Of License")
            .error((errors) => {
                return eolErrors(errors);
            }),
        otherwise: Joi.date().label("End Of License"),
    }),
    defaultCurrency: Joi.object().keys({
        value: Joi.number().label("Currency").required(),
    }),
    supportedCountryId: Joi.when(Joi.ref("$isCountryRequired"), {
        is: true,
        then: Joi.number().integer().required(),
        otherwise: Joi.optional(),
    })
        .label("Country")
        .error((errors) => {
            function errorsAsString() {
                const err = errors[0];
                switch (err.type) {
                    case "number.min":
                        return `can't be less than ${err.context.limit} users.`;
                    case "any.empty":
                        return "can't be an empty field.";
                    case "string.regex.base":
                        return "should be a number.";
                    case "number.max":
                        return "can not exceed the provider's max number of users";
                    default:
                        return "is required.";
                }
            }
            return {
                template: " {{message}}",
                context: {
                    errors: errors.length,
                    message: errorsAsString(),
                },
            };
        }),
    abbreviation: Joi.string()
        .min(2)
        .max(5)
        .optional()
        .allow("")
        .regex(/^[a-zA-Z0-9]*$/)
        .regex(/^\S*$/)
        .label("Abbreviation")
        .error((errors) => {
            function errorsAsString() {
                const err = errors[0];
                switch (err.type) {
                    case "string.regex.base":
                        return "should not include spaces";
                    case "string.min":
                        return "can't be less than 2 letters";
                    case "string.max":
                        return "can not exceed 5 letters";
                    default:
                        break;
                }
            }
            return {
                template: " {{message}}",
                context: {
                    errors: errors.length,
                    message: errorsAsString(),
                },
            };
        }),
    externalId: Joi.string()
        .min(2)
        .optional()
        .allow("")
        .label("ExternalId")
        .error((errors) => {
            return nameErrorsAsString(errors);
        }),
    preferences: Joi.when(Joi.ref("$isSuperAdminAndParent"), {
        is: true,
        then: Joi.array().items(
            Joi.object().keys({
                entity: Joi.object()
                    .allow(null)
                    .label("Preference Entity")
                    .error((errors) => {
                        const err = errors[0];
                        let strError = "";
                        switch (err.type) {
                            case "any.empty":
                                strError = "is required";
                                break;
                            case "any.allowOnly":
                                strError = "is invalid";
                                break;
                        }
                        return {
                            template: " {{message}}",
                            context: {
                                errors: errors.length,
                                message: strError,
                            },
                        };
                    }),
                localizations: Joi.when(Joi.ref("entity"), {
                    is: Joi.any().valid(null, ""),
                    then: Joi.optional(),
                    otherwise: Joi.object().keys({
                        en: Joi.string()
                            .trim()
                            .required()
                            .label("English Value"),
                    }),
                }),
            })
        ),
        otherwise: Joi.optional(),
    }),
});

const EditOrganizationSchema = Joi.object().keys({
    name: Joi.string()
        .trim()
        .max(256)
        .min(3)
        .regex(/[\w[\]`!@#$%^&*()={}:;<>+'-]*/)
        .required()
        .label("Name")
        .error((errors) => {
            return nameErrorsAsString(errors);
        }),

    currentActiveUsers: Joi.number().integer().min(0),
    maxUsersNumber: Joi.number()
        .integer()
        .positive()
        .min(1)
        .required()
        .label("Max Users Number")
        .error((errors) => {
            function errorsAsString() {
                const err = errors[0];
                switch (err.type) {
                    case "any.empty":
                        return "can't be an empty field.";
                    case "string.regex.base":
                        return "should be a number.";
                    case "number.positive":
                        return "can't be zero or a negative number";
                    case "number.min":
                        return "can't be less than current active users";
                    case "number.max":
                        return "can not exceed the provider's max number of users";
                    default:
                        return "is required.";
                }
            }
            return {
                template: " {{message}}",
                context: {
                    errors: errors.length,
                    message: errorsAsString(),
                },
            };
        }),
    type: Joi.string()
        .required()
        .label("Type")
        .error((errors) => {
            return requiredErrorsAsString(errors);
        }),

    endOfLicense: Joi.when(Joi.ref("$isSuperAdmin"), {
        is: true,
        then: Joi.date()
            .required()
            .label("End Of License")
            .error((errors) => {
                return eolErrors(errors);
            }),
        otherwise: Joi.date().label("End Of License"),
    }),
    defaultCurrency: Joi.object().keys({
        value: Joi.number().label("Currency").required(),
    }),
    supportedCountryId: Joi.when(Joi.ref("$isCountryRequired"), {
        is: true,
        then: Joi.number().integer().required(),
        otherwise: Joi.optional(),
    })
        .label("Country")
        .error((errors) => {
            function errorsAsString() {
                const err = errors[0];
                switch (err.type) {
                    case "number.min":
                        return `can't be less than ${err.context.limit} users.`;
                    case "any.empty":
                        return "can't be an empty field.";
                    case "string.regex.base":
                        return "should be a number.";
                    case "number.max":
                        return "can not exceed the provider's max number of users";
                    default:
                        return "is required.";
                }
            }
            return {
                template: " {{message}}",
                context: {
                    errors: errors.length,
                    message: errorsAsString(),
                },
            };
        }),
    abbreviation: Joi.string()
        .min(2)
        .max(5)
        .optional()
        .allow("")
        .regex(/^[a-zA-Z0-9]*$/)
        .regex(/^\S*$/)
        .label("Abbreviation")
        .error((errors) => {
            function errorsAsString() {
                const err = errors[0];
                switch (err.type) {
                    case "string.regex.base":
                        return "should not include spaces";
                    case "string.min":
                        return "can't be less than 3 letters";
                    case "string.max":
                        return "can not exceed 5 letters";
                    default:
                        break;
                }
            }
            return {
                template: " {{message}}",
                context: {
                    errors: errors.length,
                    message: errorsAsString(),
                },
            };
        }),
    externalId: Joi.string()
        .min(2)
        .optional()
        .allow(null)
        .allow("")
        .label("ExternalId")
        .error((errors) => {
            return nameErrorsAsString(errors);
        }),
    preferences: Joi.when(Joi.ref("$isSuperAdminAndParent"), {
        is: true,
        then: Joi.array().items(
            Joi.object().keys({
                entity: Joi.object()
                    .allow(null)
                    .label("Preference Entity")
                    .error((errors) => {
                        const err = errors[0];
                        let strError = "";
                        switch (err.type) {
                            case "any.empty":
                                strError = "is required";
                                break;
                            case "any.allowOnly":
                                strError = "is invalid";
                                break;
                        }
                        return {
                            template: " {{message}}",
                            context: {
                                errors: errors.length,
                                message: strError,
                            },
                        };
                    }),
                localizations: Joi.when(Joi.ref("entity"), {
                    is: Joi.any().valid(null, ""),
                    then: Joi.optional(),
                    otherwise: Joi.object().keys({
                        en: Joi.string()
                            .trim()
                            .required()
                            .label("English Value"),
                    }),
                }),
            })
        ),
        otherwise: Joi.optional(),
    }),
});

export default { AddOrganizationSchema, EditOrganizationSchema };
